import { GroupPlanView, OfficePlanView } from './plan-result';

/**
 * Las entregas (sub-rutas) de una corrida, como filtro seleccionable de la lista.
 *
 * Existe porque la lista solo se podía filtrar por **texto**: "¿qué muestra la
 * ENTREGA 301?" se contestaba tipeando su nombre y confiando en que ninguna otra
 * lo contuviera. El mapa sí tenía un selector de rutas, pero recorta el `geojson`
 * y es de una a la vez —para poder seguir un solo recorrido— así que no se podía
 * reusar tal cual.
 *
 * La llave es la del **grupo de planificación**, no la etiqueta: cuando la
 * salvaguarda de tamaño parte un grupo por municipio la etiqueta se vuelve
 * `ENTREGA 301 · COMAYAGUA` y deja de emparejar. Ver `planningGroupKeyOf`.
 *
 * Módulo puro: entran las secciones por oficina, salen los tags. Sin HTTP, sin
 * estado de componente, sin Angular.
 */

/** Una entrega seleccionable. */
export interface RouteTag {
  /** Llave del grupo de planificación; empareja tarjetas y puntos. */
  key: string;
  /** Nombre de la entrega, como lo nombra operaciones (`ENTREGA 301`). */
  label: string;
  /** Color de la ruta, el mismo de su tarjeta y de sus pines. */
  color: string;
  /** Paradas que el motor recibió para esta entrega. */
  stops: number;
}

/** Color de respaldo cuando el grupo no llegó a tener uno asignado. */
const FALLBACK_COLOR = '#94a3b8'; // slate-400: neutro, no compite con la paleta

/**
 * Tags de la corrida, en el orden en que aparecen las oficinas y sus grupos.
 *
 * Se construyen desde los **grupos**, que son la unidad de planificación real, y
 * no desde las tarjetas: un grupo que falló o que no produjo ruta igual es una
 * entrega de la corrida, y esconderla del filtro haría que sus no-asignados
 * quedaran inalcanzables.
 *
 * Se funden por llave sumando paradas: dos oficinas pueden repartir la misma
 * sub-ruta, y en ese caso es UNA entrega vista dos veces, no dos entregas.
 *
 * Sin orden alfabético a propósito: el orden de los grupos es el de la corrida, y
 * es el mismo en el que aparecen las tarjetas más abajo. Reordenar acá obligaría
 * a buscar cada tag en otra secuencia que la de la lista que filtra.
 */
export function buildRouteTags(views: readonly OfficePlanView[]): RouteTag[] {
  const byKey = new Map<string, RouteTag>();
  for (const view of views ?? []) {
    for (const group of view?.groups ?? []) {
      const existing = byKey.get(group.key);
      if (existing) {
        existing.stops += group.stops ?? 0;
        continue;
      }
      byKey.set(group.key, {
        key: group.key,
        label: labelOf(group),
        color: group.color ?? FALLBACK_COLOR,
        stops: group.stops ?? 0,
      });
    }
  }
  return [...byKey.values()];
}

/**
 * Nombre de la entrega: el `label` del grupo, tal cual.
 *
 * Ese `label` YA resuelve el concepto correcto —la RUTA, y el territorio solo
 * cuando varias rutas lo comparten (ver `GroupPlanView.label`)—, así que acá no hay
 * nada que decidir. La regla vive donde se conocen los nombres de las rutas del
 * territorio, no en el rotulador del tag.
 *
 * Antes recortaba el sufijo ` · <municipio>` que la salvaguarda de tamaño le ponía a
 * los pedazos de una sub-ruta grande. Esa salvaguarda no existe en la partición por
 * territorio —partir un territorio partiría también su flota—, así que no hay sufijo
 * que quitar y el recorte solo podía truncar en silencio un nombre que lo llevara.
 */
function labelOf(group: GroupPlanView): string {
  return group.label;
}

/**
 * ¿La llave pasa el filtro de entregas?
 *
 * Con ningún tag seleccionado **pasa todo**: la ausencia de filtro no filtra, el
 * mismo criterio que `matchesSearch` con consulta vacía.
 */
export function passesRouteTags(selected: ReadonlySet<string>, routeKey: string): boolean {
  return selected.size === 0 || selected.has(routeKey);
}
